import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authService } from '../services/auth.service';
import { User, LoginRequest, RegisterRequest } from '../types';
import { TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY } from '../utils/constants';
import { clearAuthStorage, noteLoggedInUser } from '../utils/authStorage';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  rateLimitRetryAfter: number | null; // seconds until login retry allowed
  themeMode: 'light' | 'dark';
}

const initialState: AuthState = {
  user: JSON.parse(localStorage.getItem(USER_KEY) || 'null'),
  token: localStorage.getItem(TOKEN_KEY),
  isAuthenticated: !!localStorage.getItem(TOKEN_KEY),
  loading: false,
  error: null,
  rateLimitRetryAfter: null,
  themeMode: (localStorage.getItem('themeMode') as 'light' | 'dark') || 'light'
};

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      return response.data;
    } catch (err: any) {
      if (err.response?.status === 429) {
        const retryAfter = err.response.data?.error?.retryAfter
          || parseInt(err.response.headers['retry-after'] || '900', 10);
        return rejectWithValue({ message: 'Too many login attempts. Please try again later.', retryAfter });
      }
      const message = err.response?.data?.error?.message || err.message || 'Login failed';
      return rejectWithValue({ message });
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (data: RegisterRequest, { rejectWithValue }) => {
    try {
      const response = await authService.register(data);
      return response.data;
    } catch (err: any) {
      const message = err.response?.data?.error?.message || err.message || 'Registration failed';
      return rejectWithValue({ message });
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  await authService.logout();
});

export const getProfile = createAsyncThunk('auth/getProfile', async () => {
  const response = await authService.getProfile();
  return response.data;
});

// CL1-CL3 / F4 — record legal acceptance for the signed-in user; backend
// stamps legalAcceptedAt + legalAcceptedVersion and returns the new
// flags so we can flip the cached User.legalAccepted to true.
export const acceptLegal = createAsyncThunk('auth/acceptLegal', async (_, { rejectWithValue }) => {
  try {
    const response = await authService.acceptLegal();
    return response.data;
  } catch (err: any) {
    const message = err.response?.data?.error?.message || err.message || 'Could not record acceptance';
    return rejectWithValue({ message });
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<{ user: User; token: string; refreshToken: string }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      noteLoggedInUser((action.payload.user as any)?.id); // account switch → wipe prev prefs
      localStorage.setItem(USER_KEY, JSON.stringify(action.payload.user));
      localStorage.setItem(TOKEN_KEY, action.payload.token);
      localStorage.setItem(REFRESH_TOKEN_KEY, action.payload.refreshToken);
    },
    clearCredentials: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    },
    setThemeMode: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.themeMode = action.payload;
      localStorage.setItem('themeMode', action.payload);
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        // H11 / F7 — 2FA branch returns { requires2FA, challengeToken } with
        // no user. Don't write to localStorage in that case; the LoginForm
        // handles the redirect to the challenge step from its own state.
        if (action.payload && (action.payload as any).user) {
          state.user = (action.payload as any).user;
          state.token = (action.payload as any).token;
          state.isAuthenticated = true;
          noteLoggedInUser((action.payload as any).user?.id); // account switch → wipe prev prefs
          localStorage.setItem(USER_KEY, JSON.stringify((action.payload as any).user));
          localStorage.setItem(TOKEN_KEY, (action.payload as any).token);
          localStorage.setItem(REFRESH_TOKEN_KEY, (action.payload as any).refreshToken);
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.error = payload?.message || action.error.message || 'Login failed';
        state.rateLimitRetryAfter = payload?.retryAfter ?? null;
      })
      // Register — server returns pending:true, no token issued
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state) => {
        state.loading = false;
        // No token stored — user must wait for admin approval
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.error = payload?.message || action.error.message || 'Registration failed';
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        // Session keys ONLY — chart/indicator/★ preferences survive a re-login
        // (localStorage.clear() here was why a VPS restart reset the chart page).
        clearAuthStorage();
      })
      // Get Profile
      .addCase(getProfile.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload;
          localStorage.setItem(USER_KEY, JSON.stringify(action.payload));
        }
      })
      // CL1-CL3 / F4 — flip the cached user's legalAccepted flag so the
      // gate disappears without requiring a re-login.
      .addCase(acceptLegal.fulfilled, (state, action) => {
        if (state.user) {
          state.user = { ...state.user, legalAccepted: true, legalVersion: action.payload?.legalVersion };
          localStorage.setItem(USER_KEY, JSON.stringify(state.user));
        }
      });
  }
});

export const { setCredentials, clearCredentials, setThemeMode } = authSlice.actions;
export default authSlice.reducer;
